import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { CdsTabsModule } from '@celerofinancas/design-system';
import {
  BfmCashFlowFiltersComponent,
  BfmPaginatedCashFlowComponent,
  CASH_FLOW_FILTERS_TOKEN,
  CashFlowUtilsService,
  ColumnItem,
  FINANCIAL_RECORD_LIST_TOKEN,
} from '@celerofinancas/ui-cash-flow';
import { CommonInputsModule } from '@celerofinancas/ui-common';
import { ModalService } from '@celerofinancas/ui-modals';
import { CheckingAccountState } from '@celerofinancas/state-management';
import { FeatureFlagService } from '@celerofinancas/ui-feature-flag';
import { DdaModalComponent } from '@shared/components/dda-modal/dda-modal.component';
import { HeaderAccountsComponent } from '@shared/components/header-accounts/header-accounts.component';
import { MainTitleComponent } from '@shared/components/main-title/main-title.component';
import { NavigationBarComponent } from '@shared/components/navigation-bar/navigation-bar.component';
import { BootstrapCoordinator } from '@shared/models/bootstrap-coordinator';
import { map, Observable } from 'rxjs';
import { AsyncPipe } from '@angular/common';

/**
 * CashFlowComponent is a component that represents the cash flow page
 */
@Component({
  selector: 'caixa-webcomponent-cash-flow',
  standalone: true,
  imports: [
    MainTitleComponent,
    NavigationBarComponent,
    HeaderAccountsComponent,
    BfmPaginatedCashFlowComponent,
    BfmCashFlowFiltersComponent,
    CommonInputsModule,
    CdsTabsModule,
    AsyncPipe,
  ],
  providers: [
    {
      provide: CASH_FLOW_FILTERS_TOKEN,
      useValue: { tabsOrder: ['done', 'predicted'], defaultReportType: 'done' },
    },
    {
      provide: FINANCIAL_RECORD_LIST_TOKEN,
      useValue: {
        recordNameFn: (
          r: { name?: string; bank_id?: string; company_related_name?: string | null }
        ) =>
          r.bank_id === '104' && r.company_related_name
            ? `${r.name} - ${r.company_related_name}`
            : r.name,
        nameMaxLines: 2,
      },
    },
  ],
  templateUrl: './cash-flow.component.html',
  styleUrl: './cash-flow.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CashFlowComponent extends BootstrapCoordinator implements OnInit {
  /**
   * Template for the magnifier icon
   */
  @ViewChild('magnifierTemplate', { read: TemplateRef, static: false })
  public magnifierTemplateRef: TemplateRef<unknown>;

  /**
   * Cash Flow Utils Service
   */
  public readonly utils = inject(CashFlowUtilsService);

  /**
   * List of columns to display in the cash flow list
   */
  private readonly cdsModal = inject(ModalService);

  /**
   * Feature flag service
   */
  private readonly featureFlag: FeatureFlagService<string> = inject(FeatureFlagService);

  /**
   * Controls whether the cash flow download action is available
   */
  public readonly isDownloadEnabled$: Observable<boolean> =
    this.featureFlag.isFlagEnabled$('front_cashflow@download');

  /**
   * List of columns to display in the financial records list
   */
  public columnsList$: Observable<ColumnItem[]>;

  /**
   * Controls whether the DDA button should be visible.
   * Exibido sempre que o usuário está na aba "Previstos" do Fluxo de Caixa.
   */
  public readonly showDdaButton$ = this.utils.filters$.pipe(
    map(({ is_predicted }) => !!is_predicted),
  );

  /**
   * Headers for the cash flow table
   */
  public headers = {
    name: 'Nome',
    due_at: 'Vencimento',
    paid_at: 'Liquidação',
    coa_name: 'Categoria',
    value: 'Valor (R$)',
    bank_id: 'Banco e Conta',
    magnifier: '',
    payment_method: 'Método de Pagamento',
  };

  /**
   * Lifecycle hook that is called after the component has been initialized.
   */
  public override ngOnInit(): void {
    super.ngOnInit();

    this.columnsList$ = this.utils.filters$.pipe(
      map(({ is_predicted }) => [
        { name: 'payment_method', size: 1, align: 'center' },
        { name: is_predicted ? 'due_at' : 'paid_at', size: 2, align: 'center' },
        { name: 'name', size: 2, align: 'left' },
        { name: 'coa_name', size: 2, align: 'left' },
        { name: 'bank_id', size: 2, align: 'center' },
        { name: 'value', size: 2, align: 'right' },
        { name: 'custom', size: 1, align: 'center', template: () => this.magnifierTemplateRef },
      ]),
    );
  }

  /**
   * Opens the DDA modal
   */
  public openModal(): void {
    // Get active accounts from state
    const accountsState = this.store.selectSnapshot(CheckingAccountState.getState);
    const accountsList = Array.isArray(accountsState)
      ? accountsState
      : ((accountsState as Record<string, unknown>)?.list as Record<string, unknown>[] || []);

    this.cdsModal.open(DdaModalComponent, {
      headerTitle: 'Boletos Previstos',
      accounts: accountsList,
      size: 'sm',
    });
  }
}
